import { FC, lazy, Suspense, useEffect } from "react"
import { matchPath, Outlet, useLocation } from "react-router-dom"

import { ULoading } from "@/components"

import { CssBaseline, Grid } from "../../components/mui.components"
import { useAppSelector } from "../../core/app/hooks"
import { RootState } from "../../core/app/store"
import { isTokenValid } from "../../core/config/okta.config"
import { useMyNavigation } from "../../hooks/useMyNavigation"

import Footer from "./Footer/Footer"
import Header from "./Header/Header"
import CsHeader from "./Header/CsHeader"

const AlertBanner = lazy(
  () => import("../../components/AlertBanner/AlertBanner"),
)

const MainLayout: FC = () => {
  const location = useLocation()
  const navigate = useMyNavigation()
  const isLogin = isTokenValid()
  const { user, systemBannerMessage, marketBannerMessage } = useAppSelector(
    (state: RootState) => state.userService,
  )

  // Helper function to check if current path matches a given pattern
  const isPathMatching = (pattern: string) => matchPath(pattern, location.pathname)

  const shouldShowHeader = () => {
    // If user can view all organizations
    if (user.isAllowToViewAllOrg) {
      // Show header if:
      // 1. Cs User is viewing a different organization than their own, AND
      // 2. Not on organization page, AND
      const isViewingDifferentOrg = user.current_orgId !== user.my_orgId
      const isNotOrgListPage = !isPathMatching("/organization")
      
      return isViewingDifferentOrg && isNotOrgListPage
    } else {
      // If user is not a Cs user, always show header
      return true
    }
  }

  // Check if we're on a clinical case page (regardless of org context)
  const isOnClinicalPage = isPathMatching("/clinical/patient/:patientId/case/:caseId/*") || isPathMatching("/org/:orgId/clinical/patient/:patientId/case/:caseId/*")


  // Final decision: show header if conditions are met AND not on clinical case page
  const showHeader = shouldShowHeader() && !isOnClinicalPage

  useEffect(() => {
    const destinationPath = `${
      location.pathname + location.search + location.hash
    }`
    if (isLogin) {
      if (location.pathname.includes("/auth") || location.pathname === "/") {
        navigate(user.isAllowToViewAllOrg ? "/organization" : "/patients")
      }
    } else {
      if (
        !location.pathname.includes("/auth") &&
        destinationPath != "/" &&
        destinationPath != "/patients" &&
        destinationPath != "/organization"
      ) {
        navigate(
          `/auth/login?redirectTo=${encodeURIComponent(destinationPath)}`,
          {
            replace: true,
          },
        )
      } else {
        navigate(`/auth/login`, {
          replace: true,
        })
      }
    }
  }, [location, user])
  return (
    <>
      {isLogin && (
        <>
          <CssBaseline />
          {systemBannerMessage?.id && (
            <Suspense fallback={<ULoading isLoading={true} />}>
              <AlertBanner
                bannerInfo={systemBannerMessage}
                isMarketingBanner={false}
              />
            </Suspense>
          )}
          {user.isAllowToViewAllOrg && <CsHeader />}
          {/* Keep Header mounted but hidden with CSS to prevent remounting.
              Remounting causes all refs/state to reset, triggering unnecessary API calls in Header's useEffect.
              Using display: none preserves component state during navigation. */}
          <div style={{ display: showHeader ? 'block' : 'none' }}>
            <Header />
          </div>

          <Grid
            sx={{
              background: "#F7F8F9",
              flexGrow: 1,
            }}
            container
            component={"main"}
          >
            <Outlet />
          </Grid>
          {!location.pathname.includes("/clinical/") && (
            <>
              <Footer />
              {marketBannerMessage?.id && (
                <Suspense fallback={<ULoading isLoading={true} />}>
                  <AlertBanner
                    bannerInfo={marketBannerMessage}
                    isMarketingBanner={true}
                  />
                </Suspense>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}

export default MainLayout
